import type { Bullet } from '../combat/bullet';
import { aabbHit, type Box } from './collisions';

export const BULLET_W = 4;
export const BULLET_H = 16;

export interface EnemyHit extends Box {
  id: number;
  row: number;
}

export interface CombatResolveInput {
  bullets: readonly Bullet[];
  enemies: readonly EnemyHit[];
  player: Box;
}

export interface CombatResolveResult {
  bullets: Bullet[];
  killedEnemyIds: number[];
  scoreDelta: number;
  livesDelta: number;
  enemyHitCount: number;
  playerHitCount: number;
}

export function pointsForRow(row: number): number {
  if (row <= 0) return 30;
  if (row <= 2) return 20;
  return 10;
}

export function bulletBox(bullet: Bullet): Box {
  return {
    x: bullet.x - BULLET_W / 2,
    y: bullet.y - BULLET_H / 2,
    w: BULLET_W,
    h: BULLET_H,
  };
}

export function resolveCollisions(input: CombatResolveInput): CombatResolveResult {
  const remainingBullets: Bullet[] = [];
  const killed = new Set<number>();
  let scoreDelta = 0;
  let livesDelta = 0;
  let enemyHitCount = 0;
  let playerHitCount = 0;

  for (const bullet of input.bullets) {
    const bbox = bulletBox(bullet);

    if (bullet.owner === 'player') {
      let hit: EnemyHit | null = null;
      for (const enemy of input.enemies) {
        if (killed.has(enemy.id)) continue;
        if (aabbHit(bbox, enemy)) {
          hit = enemy;
          break;
        }
      }
      if (hit) {
        killed.add(hit.id);
        scoreDelta += pointsForRow(hit.row);
        enemyHitCount += 1;
        continue;
      }
      remainingBullets.push(bullet);
      continue;
    }

    if (aabbHit(bbox, input.player)) {
      livesDelta -= 1;
      playerHitCount += 1;
      continue;
    }
    remainingBullets.push(bullet);
  }

  return {
    bullets: remainingBullets,
    killedEnemyIds: Array.from(killed),
    scoreDelta,
    livesDelta,
    enemyHitCount,
    playerHitCount,
  };
}
